/**
 * Unify ChargeItem model.
 *
 * Represents a billable charge for a clinical service (session, group, etc.).
 * ChargeItems are created from Encounters and feed into the billing workflow,
 * linking sessions to the Stripe billing pipeline via Account references.
 *
 * ┌──────────────────────┬───────────────────────────────────────────────────────────┐
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ status               │ "planned" | "billable" | "not-billable" | "aborted"       │
 * │                      │   | "billed" | "entered-in-error" | "unknown"             │
 * │ code                 │ CodeableConcept — service code (CPT/HCPCS)                │
 * │ subject              │ Reference<Patient> — the billed patient                   │
 * │ context              │ Reference<Encounter> — the triggering encounter           │
 * │ occurrenceDateTime   │ ISO datetime — when the service occurred                  │
 * │ performer[]          │ Who performed the service                                 │
 * │  .actor              │ Reference<Practitioner> — the rendering provider          │
 * │ performingOrganization│ Reference<Organization> — the billing practice           │
 * │ quantity             │ Quantity — units of service (typically 1)                 │
 * │ account[]            │ Reference<Account>[] — billing account(s)                 │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * Extension summary:
 *   (none — ChargeItem uses standard FHIR fields only)
 *
 * Relationships:
 *   - ChargeItem.context → Encounter (one charge per encounter)
 *   - ChargeItem.subject → Patient
 *   - ChargeItem.account[] → Account (Stripe billing accounts)
 *   - ChargeItem.performingOrganization → Organization (practice)
 */
import type { ChargeItem, Extension, Reference } from '@medplum/fhirtypes';
import { BILLING_GROUPING_KEY_EXT } from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** ChargeItem statuses used by Unify. */
export type ChargeItemStatus =
  | 'planned'
  | 'billable'
  | 'not-billable'
  | 'aborted'
  | 'billed'
  | 'entered-in-error'
  | 'unknown';

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the billed patient reference. */
export function getChargeItemSubjectRef(item: ChargeItem): Reference | undefined {
  return item.subject;
}

/** Returns the triggering Encounter reference. */
export function getChargeItemEncounterRef(item: ChargeItem): Reference | undefined {
  return item.context;
}

/** Returns the first billing Account reference. */
export function getChargeItemAccountRef(item: ChargeItem): Reference | undefined {
  return item.account?.[0];
}

/** Returns the performing Organization reference. */
export function getChargeItemOrgRef(item: ChargeItem): Reference | undefined {
  return item.performingOrganization;
}

/** Returns the first performer (Practitioner) reference. */
export function getChargeItemPerformerRef(item: ChargeItem): Reference | undefined {
  return item.performer?.[0]?.actor;
}

/** Returns the service code string (e.g. CPT code). */
export function getChargeItemCode(item: ChargeItem): string | undefined {
  return item.code?.coding?.[0]?.code;
}

/** Returns priceOverride amount (USD) when set. */
export function getChargeItemPrice(item: ChargeItem): number | undefined {
  return item.priceOverride?.value;
}

/** Returns the first definitionUri (canonical ChargeItemDefinition.url). */
export function getChargeItemDefinitionUri(item: ChargeItem): string | undefined {
  return item.definitionUri?.[0];
}

/** Returns the ChargeItem status. */
export function getChargeItemStatus(item: ChargeItem): ChargeItemStatus | undefined {
  return item.status as ChargeItemStatus | undefined;
}

/** Returns whether the charge is billable. */
export function isChargeItemBillable(item: ChargeItem): boolean {
  return item.status === 'billable';
}

export function getBillingGroupingKey(item: ChargeItem): string | undefined {
  return item.extension?.find((e) => e.url === BILLING_GROUPING_KEY_EXT)?.valueString;
}

/** Write-once grouping key. Returns the item unchanged if already set to the same value. */
export function setBillingGroupingKey(item: ChargeItem, key: string): ChargeItem {
  const existing = getBillingGroupingKey(item);
  if (existing === key) return item;
  if (existing && existing !== key) {
    throw new Error(`billing-grouping-key already set to ${existing}`);
  }
  const rest = (item.extension ?? []).filter((e) => e.url !== BILLING_GROUPING_KEY_EXT);
  const next: Extension = { url: BILLING_GROUPING_KEY_EXT, valueString: key };
  return { ...item, extension: [...rest, next] };
}
